import { useFrame } from "@react-three/fiber";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import * as THREE from "three/webgpu";
import { createOverlayFaceMaterial } from "./editDots.ts";
import { useDispose } from "./hooks/useDispose.ts";
import { noRaycast } from "./sharedMaterials.ts";
import {
  getSurfaceSelectionHighlightSnapshot,
  type SurfaceSelectionHighlightSurface,
  subscribeSurfaceSelectionHighlight,
} from "./surfaceSelectionHighlight.ts";

const SELECTED_SURFACE_COLOR = "#ffcc00";
const HOVER_SURFACE_COLOR = "#ffee66";
const HOVER_SURFACE_OPACITY = 0.14;
const HOVER_FADE_SPEED = 14;
const HOVER_CLEAR_OPACITY = 0.004;

export function SurfaceSelectionHighlight() {
  const snapshot = useSyncExternalStore(
    subscribeSurfaceSelectionHighlight,
    getSurfaceSelectionHighlightSnapshot,
    getSurfaceSelectionHighlightSnapshot,
  );

  const selected = snapshot.selected;
  const hover =
    snapshot.hover &&
    !selected.some((surface) => surface.key === snapshot.hover?.key)
      ? snapshot.hover
      : null;
  const [renderedHover, setRenderedHover] =
    useState<SurfaceSelectionHighlightSurface | null>(hover);
  const hoverOpacityRef = useRef(hover ? HOVER_SURFACE_OPACITY : 0);

  const selectedGeometry = useMemo(
    () => buildSurfaceGeometry(selected),
    [selected],
  );
  const hoverGeometry = useMemo(
    () => buildSurfaceGeometry(renderedHover ? [renderedHover] : []),
    [renderedHover],
  );
  const selectedMaterial = useMemo(
    () =>
      createOverlayFaceMaterial({
        color: SELECTED_SURFACE_COLOR,
        opacity: 0.18,
        depthTest: true,
        side: THREE.DoubleSide,
      }),
    [],
  );
  const hoverMaterial = useMemo(
    () =>
      createOverlayFaceMaterial({
        color: HOVER_SURFACE_COLOR,
        opacity: hoverOpacityRef.current,
        depthTest: true,
        side: THREE.DoubleSide,
      }),
    [],
  );

  useDispose(selectedGeometry);
  useDispose(hoverGeometry);
  useDispose(selectedMaterial);
  useDispose(hoverMaterial);

  useEffect(() => {
    if (!hover) return;
    hoverOpacityRef.current = 0;
    hoverMaterial.opacity = 0;
    setRenderedHover(hover);
  }, [hover, hoverMaterial]);

  useFrame((_state, delta) => {
    if (!renderedHover) return;
    const target = hover ? HOVER_SURFACE_OPACITY : 0;
    const next = THREE.MathUtils.damp(
      hoverOpacityRef.current,
      target,
      HOVER_FADE_SPEED,
      delta,
    );
    hoverOpacityRef.current =
      Math.abs(next - target) < HOVER_CLEAR_OPACITY ? target : next;
    hoverMaterial.opacity = hoverOpacityRef.current;
    if (!hover && hoverOpacityRef.current <= HOVER_CLEAR_OPACITY) {
      hoverOpacityRef.current = 0;
      hoverMaterial.opacity = 0;
      setRenderedHover(null);
    }
  });

  return (
    <>
      {selectedGeometry ? (
        <mesh
          geometry={selectedGeometry}
          material={selectedMaterial}
          renderOrder={2}
          raycast={noRaycast}
          userData={{ excludeFromExport: true }}
        />
      ) : null}
      {hoverGeometry ? (
        <mesh
          geometry={hoverGeometry}
          material={hoverMaterial}
          renderOrder={3}
          raycast={noRaycast}
          userData={{ excludeFromExport: true }}
        />
      ) : null}
    </>
  );
}

function buildSurfaceGeometry(
  surfaces: readonly SurfaceSelectionHighlightSurface[],
): THREE.BufferGeometry | null {
  let triangleCount = 0;
  for (const surface of surfaces) {
    for (const face of surface.faces) {
      if (face.vertices.length >= 3) {
        triangleCount += face.vertices.length - 2;
      }
    }
  }
  if (triangleCount === 0) return null;

  const positions = new Float32Array(triangleCount * 9);
  let w = 0;
  for (const surface of surfaces) {
    for (const face of surface.faces) {
      const vertices = face.vertices;
      if (vertices.length < 3) continue;
      const origin = vertices[0]!;
      for (let i = 1; i < vertices.length - 1; i += 1) {
        const a = vertices[i]!;
        const b = vertices[i + 1]!;
        positions[w++] = origin[0];
        positions[w++] = origin[1];
        positions[w++] = origin[2];
        positions[w++] = a[0];
        positions[w++] = a[1];
        positions[w++] = a[2];
        positions[w++] = b[0];
        positions[w++] = b[1];
        positions[w++] = b[2];
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geometry;
}
